import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useConstructionProjects } from "../../api";
import { BeaconBase, Project, SiteWithBeacons } from "../../api/models";
import { useRefreshAll } from "../../util";
import "./construction-project.scss";
import { ConstructionProjectDrawer } from "../../components/drawers/project/ConstructionProjectDrawer";
import { ConstructionSiteDrawer } from "../../components/drawers/site/ConstructionSiteDrawer";
import { ConstructionProjectMap } from "../../components/map/ConstructionProjectMap";
import { useNodeSelectedContext } from "../../contexts/NodeSelectedContext";
import { ConstructionProjectTree } from "../../components/project-tree/ConstructionProjectTree";

interface HistoryState {
  selectedId: string;
}

export interface BeaconsWithProjectSite extends BeaconBase {
  projectId: string;
  siteId: string;
  projectName: string;
  siteName: string;
}

export function ConstructionProjectWrapper() {
  const [refreshTimeStamp, setRefreshTimeStamp] = useState<string>("");
  const { selectedNode, setSelectedNode } = useNodeSelectedContext();
  const history = useHistory();

  useEffect(() => {
    if (refreshTimeStamp) {
      refreshAll();
    }
  }, [refreshTimeStamp]);
  /*
    When user is navigating to the construction-project page from a different page after 
    they have clicked on a project or site name on that page (/beaons & /beacon-details), 
    get which project or site was clicked on and set the selectedNode state to that project 
    or site.

    This will set the selected node upon load of the construction project page which will 
    collapse all other nodes and turn other node visible off while having the selected 
    node expanded and visible.
  */
  useEffect(() => {
    if (
      history.location.state &&
      typeof history.location.state === "object" &&
      "selectedId" in history.location.state
    ) {
      const state = history.location.state as HistoryState;
      setSelectedNode({ selectedId: state.selectedId });
    }
  }, [history]);

  const projects = useConstructionProjects(true);

  const sites = projects.value?.items?.flatMap((project) => project.sites);

  const beacons = sites?.flatMap((site) => site.beacons);

  const beaconsWithProjSite: BeaconsWithProjectSite[] | undefined =
    useMemo(() => {
      return projects.value?.items?.flatMap((project) =>
        project.sites.flatMap((site) =>
          site.beacons.map((beacon) => ({
            ...beacon,
            projectId: project.id,
            siteId: site.id,
            projectName: project.name,
            siteName: site.name,
          }))
        )
      );
    }, [projects.value?.items]);

  const selectedProject = useMemo(() => {
    return projects.value?.items?.find(
      (project: Project) => project.id === selectedNode.selectedProjectId
    );
  }, [selectedNode.selectedProjectId]);

  const selectedSite = useMemo(() => {
    return sites?.find(
      (site: SiteWithBeacons) => site.id === selectedNode.selectedSiteId
    );
  }, [selectedNode.selectedSiteId]);

  const refreshAll = useRefreshAll(projects);

  return (
    <div className="project-container">
      <div className="column-one">
        <ConstructionProjectTree
          projects={projects.value?.items}
          isLoading={projects.loading}
          defaultIdExpanded={{
            id: selectedNode.selectedId ?? "",
            type: selectedNode.selectedType ?? "",
          }}
        />
      </div>
      <div className="column-two">
        <ConstructionProjectMap
          beacons={beacons}
          constructionSites={sites}
          constructionProjects={projects.value?.items}
          isLoading={projects.loading}
        />
      </div>
      <ConstructionProjectDrawer
        constructionProject={selectedProject}
        drawerVisible={selectedNode.projectDrawerVisible ?? false}
        onSave={(refreshTimeStamp: string) =>
          setRefreshTimeStamp(refreshTimeStamp)
        }
      />
      <ConstructionSiteDrawer
        constructionSite={selectedSite}
        constructionProjectId={selectedNode.selectedProjectId ?? ""}
        constructionProjectName={selectedNode.selectedProjectName ?? ""}
        organizationId={selectedNode.selectedOrgId ?? ""}
        beacons={beaconsWithProjSite}
        drawerVisible={selectedNode.siteDrawerVisible ?? false}
        onSave={(refreshTimeStamp: string) =>
          setRefreshTimeStamp(refreshTimeStamp)
        }
      />
    </div>
  );
}
